/** Unicode-scalar OT. Same retain/insert/delete protocol as mobile and Django. */
export type Part = number | string | { delete: number };
export type Operation = Part[];
const kind = (p: Part) =>
  typeof p === 'string' ? 'insert' : typeof p === 'number' ? 'retain' : 'delete';
const size = (p: Part) =>
  typeof p === 'string' ? Array.from(p).length : typeof p === 'number' ? p : p.delete;
function push(out: Operation, p: Part) {
  if (!size(p)) return;
  const last = out.at(-1);
  if (last !== undefined && kind(last) === kind(p)) {
    out[out.length - 1] =
      typeof p === 'string'
        ? String(last) + p
        : typeof p === 'number'
          ? size(last) + p
          : { delete: size(last) + p.delete };
  } else out.push(p);
}
export const baseLength = (op: Operation) =>
  op.reduce<number>((n, p) => n + (typeof p === 'string' ? 0 : size(p)), 0);
export const targetLength = (op: Operation) =>
  op.reduce<number>((n, p) => n + (kind(p) === 'delete' ? 0 : size(p)), 0);
export function validate(raw: unknown, max = 15000): Operation {
  if (!Array.isArray(raw) || raw.length > 10000) throw Error('Invalid operation');
  const out: Operation = [];
  for (const p of raw) {
    if (typeof p === 'string') {
      if (
        !p ||
        Array.from(p).some(
          (c) =>
            c.codePointAt(0) === 0 || (c.codePointAt(0)! >= 0xd800 && c.codePointAt(0)! <= 0xdfff),
        )
      )
        throw Error('Invalid text');
    } else if (typeof p === 'number') {
      if (!Number.isInteger(p) || p <= 0) throw Error('Invalid retain');
    } else if (!p || Object.keys(p).length !== 1 || !Number.isInteger(p.delete) || p.delete <= 0)
      throw Error('Invalid delete');
    push(out, p as Part);
  }
  if (baseLength(out) > max || targetLength(out) > max) throw Error('Text exceeds limit');
  return out;
}
export function apply(op: Operation, body: string) {
  const chars = Array.from(body);
  if (chars.length !== baseLength(op)) throw Error('Operation base mismatch');
  let cursor = 0,
    result = '';
  for (const p of op) {
    if (typeof p === 'string') result += p;
    else {
      if (typeof p === 'number') result += chars.slice(cursor, cursor + p).join('');
      cursor += size(p);
    }
  }
  return result;
}
export function replace(before: string, after: string): Operation {
  const a = Array.from(before),
    b = Array.from(after);
  let start = 0,
    end = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  while (
    end < a.length - start &&
    end < b.length - start &&
    a[a.length - end - 1] === b[b.length - end - 1]
  )
    end++;
  const out: Operation = [];
  push(out, start);
  push(out, { delete: a.length - start - end });
  push(out, b.slice(start, b.length - end).join(''));
  push(out, end);
  return validate(out);
}
export function transform(left: Operation, right: Operation): [Operation, Operation] {
  if (baseLength(left) !== baseLength(right)) throw Error('Concurrent base mismatch');
  let li = 0,
    ri = 0,
    lo = 0,
    ro = 0;
  const l: Operation = [],
    r: Operation = [];
  while (li < left.length || ri < right.length) {
    const a = left[li],
      b = right[ri];
    if (typeof b === 'string') {
      push(l, size(b));
      push(r, b);
      ri++;
    } else if (typeof a === 'string') {
      push(l, a);
      push(r, size(a));
      li++;
    } else {
      if (a === undefined || b === undefined) throw Error('Mismatched operation tails');
      const n = Math.min(size(a) - lo, size(b) - ro);
      if (typeof a === 'number' && typeof b === 'number') {
        push(l, n);
        push(r, n);
      } else if (typeof a !== 'number' && typeof b === 'number') push(l, { delete: n });
      else if (typeof a === 'number' && typeof b !== 'number') push(r, { delete: n });
      lo += n;
      ro += n;
      if (lo === size(a)) {
        li++;
        lo = 0;
      }
      if (ro === size(b)) {
        ri++;
        ro = 0;
      }
    }
  }
  return [l, r];
}
export function compose(first: Operation, second: Operation): Operation {
  if (targetLength(first) !== baseLength(second)) throw Error('Sequential base mismatch');
  let ai = 0,
    bi = 0,
    ao = 0,
    bo = 0;
  const out: Operation = [];
  while (ai < first.length || bi < second.length) {
    const a = first[ai],
      b = second[bi];
    if (a !== undefined && kind(a) === 'delete') {
      push(out, a);
      ai++;
    } else if (typeof b === 'string') {
      push(out, b);
      bi++;
    } else {
      if (a === undefined || b === undefined) throw Error('Mismatched composition tails');
      const n = Math.min(size(a) - ao, size(b) - bo);
      if (typeof a === 'number' && typeof b === 'number') push(out, n);
      else if (typeof a === 'number' && kind(b) === 'delete') push(out, { delete: n });
      else if (typeof a === 'string' && typeof b === 'number')
        push(
          out,
          Array.from(a)
            .slice(ao, ao + n)
            .join(''),
        );
      ao += n;
      bo += n;
      if (ao === size(a)) {
        ai++;
        ao = 0;
      }
      if (bo === size(b)) {
        bi++;
        bo = 0;
      }
    }
  }
  return out;
}
export function position(op: Operation, pos: number) {
  let old = 0,
    next = pos;
  for (const p of op) {
    if (typeof p === 'string') {
      if (old <= pos) next += size(p);
    } else {
      if (kind(p) === 'delete' && old < pos) next -= Math.min(pos - old, size(p));
      old += size(p);
    }
  }
  return Math.max(0, Math.min(next, targetLength(op)));
}
export const scalarOffset = (text: string, utf16: number) =>
  Array.from(text.slice(0, utf16)).length;
export const utf16Offset = (text: string, scalar: number) =>
  Array.from(text).slice(0, scalar).join('').length;
